import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, of, timer } from 'rxjs';
import {
  catchError,
  exhaustMap,
  finalize,
  takeUntil,
  takeWhile,
} from 'rxjs/operators';
import { SmartLinkDecisionAction } from 'src/app/core/dtos/enums/smart-link-decision-action';
import { SmartLinkRejectReason } from 'src/app/core/dtos/enums/smart-link-reject-reason';
import { ISmartLink } from 'src/app/core/models/i-smart-link';
import {
  ISmartLinkRunContent,
  ISmartLinkRunRequest,
} from 'src/app/core/models/i-smart-link-run-request';
import {
  SmartLinkingService,
  SmartLinksFetchResult,
} from './smart-linking.service';

export const SMART_LINK_POLL_INTERVAL_MS = 2000;
export const SMART_LINK_POLL_DEADLINE_MS = 60000;
export const SMART_LINK_MAX_WAITS_FOR_OTHER_RUN = 2;
export const SMART_LINK_UNAVAILABLE_MESSAGE =
  'Smart links unavailable — try again';
export const SMART_LINK_RUN_LOST_MESSAGE =
  'The results of this scan could not be retrieved — run Smart Link again';
export const SMART_LINK_RUN_FAILED_MESSAGE =
  'This scan failed — run Smart Link again';
// A suggestion holds one live decision at a time, so a second verdict on one already decided
// can never take — unlike the message above, retrying is not the answer.
export const SMART_LINK_ALREADY_DECIDED_MESSAGE =
  'This suggestion has already been decided — the panel has been updated to match';
// Deterministic, like the message above: another link already holds the slot, so retrying cannot
// help either.
export const SMART_LINK_NOT_REVOKED_MESSAGE =
  'Something else is already linked to that record, so it could not be undone';
export const SMART_LINK_UNDO_ACTION_LABEL = 'Undo';
// NOTE: What an accept did to the article body, appended to "Added <fund>". Shown in a toast and
// spoken by the panel's live region in the same words (ADR-0009). Never "linked" of the body:
// the queue scope the fund has just moved into is called Linked (CONTEXT.md).
export const SMART_LINK_BODY_ALREADY_PRESENT_SUFFIX =
  ' — it already has a link in the article body';
export const SMART_LINK_BODY_NO_EVIDENCE_SUFFIX =
  ' — found only in the headline or standfirst, so no link was added to the article body';
export const SMART_LINK_BODY_NAME_NOT_FOUND_SUFFIX =
  ' — its exact name was not found where the article body mentions it, so no link was added';
export const SMART_LINK_BODY_UNAVAILABLE_SUFFIX =
  ' — the article body could not be reached, so no link was added';
// Shared by the panel's own button and the content status bar's, so the two can never explain
// the same refusal differently.
export const SMART_LINK_READ_ONLY_HINT =
  'Switch to edit mode to use smart links';
export const SMART_LINK_SAVE_FIRST_HINT =
  'Save this content to use smart links';
export const SMART_LINK_ALREADY_RUNNING_HINT =
  'Smart linking is already running';
export const SMART_LINK_SAVE_IN_PROGRESS_HINT = 'Wait for the save to finish';
export const SMART_LINK_READY_HINT = 'Find smart links for this content';
// Longer than a passive toast: the window has to include the reach for the action.
export const SMART_LINK_UNDO_DURATION_MS = 6000;

export type SmartLinkRunStatus =
  'idle' | 'triggering' | 'running' | 'complete' | 'error';
export type SmartLinkRunErrorReason = 'unavailable' | 'run_lost' | 'run_failed';
type SmartLinkSettledFetch = Exclude<SmartLinksFetchResult, { kind: 'running' }>;

// The article-owned capabilities a run needs. Supplied by the parent article form, so the panel
// stays the state service's only client.
export interface SmartLinkArticleGateway {
  // True while the parent form's own Save is in flight.
  isSaving(): boolean;
  getContent(): ISmartLinkRunContent;
  getSavedAt(): string | null;
}

const POLL_TICK_FAILED = Symbol('smart-link-poll-tick-failed');

// One failed poll is not the run failing — the next tick usually answers. Only this many
// consecutive failures means the feature is genuinely unreachable.
export const SMART_LINK_POLL_MAX_CONSECUTIVE_FAILURES = 3;

export interface ISmartLinkDecisionFailure {
  smartLinkId: string;
  action: SmartLinkDecisionAction;
  // A failed decide() leaves an accept's record sitting in its box with no decision behind it; a
  // failed undoDecision() already withdrew that record before asking the server to revoke. The
  // two need opposite compensation, so the payload has to say which phase failed.
  phase: 'decide' | 'undo';
  // Which message the panel should show. 'unavailable' is a genuine transport/backend failure;
  // 'already_decided' is the store refusing a verdict on a suggestion that already holds one
  // (the attempt did not take); 'not_revoked' is a revoke refused because another link holds
  // the slot. The latter two a retry can never fix, so they need their own copy.
  reason: 'unavailable' | 'already_decided' | 'not_revoked';
}

export interface ISmartLinkRunState {
  articleId: string | null;
  status: SmartLinkRunStatus;
  runId: string | null;
  smartLinks: ISmartLink[];
  decisionsInFlight: string[];
  errorReason: SmartLinkRunErrorReason | null;
}

const INITIAL_STATE: ISmartLinkRunState = {
  articleId: null,
  status: 'idle',
  runId: null,
  smartLinks: [],
  decisionsInFlight: [],
  errorReason: null,
};

export const pendingCards = (state: ISmartLinkRunState): ISmartLink[] =>
  state.smartLinks
    .filter((link) => !link.decision_action && link.confidence >= 0.5)
    .sort((a, b) => a.rank - b.rank);

export const weakPendingCards = (state: ISmartLinkRunState): ISmartLink[] =>
  state.smartLinks
    .filter((link) => !link.decision_action && link.confidence < 0.5)
    .sort((a, b) => a.rank - b.rank);

export const linkedItems = (state: ISmartLinkRunState): ISmartLink[] =>
  state.smartLinks.filter(
    (link) => link.decision_action === SmartLinkDecisionAction.ACCEPT,
  );

export const dismissedCards = (state: ISmartLinkRunState): ISmartLink[] =>
  state.smartLinks
    .filter((link) => link.decision_action === SmartLinkDecisionAction.REJECT)
    .sort((a, b) => a.rank - b.rank);

export const dismissedCount = (state: ISmartLinkRunState): number =>
  dismissedCards(state).length;

@Injectable({
  providedIn: 'root',
})
export class SmartLinkingStateService {
  private readonly stateSubject = new BehaviorSubject<ISmartLinkRunState>(
    INITIAL_STATE,
  );
  public readonly state$ = this.stateSubject.asObservable();

  // Carries what failed: a failed accept has left a record in a Related data box for the panel
  // to take back out again.
  public readonly decisionError$ = new Subject<ISmartLinkDecisionFailure>();

  private pollSub: Subscription | null = null;
  private restoreSub: Subscription | null = null;
  private isActive = false;
  // Bumped on every activate/deactivate so callbacks of requests issued for a previous article
  // recognise they are stale and never write into the current article's state.
  private activationEpoch = 0;

  constructor(private smartLinkingService: SmartLinkingService) {}

  activate(articleId: string): void {
    const current = this.stateSubject.value;
    if (this.isActive && current.articleId === articleId) {
      return;
    }
    // NOTE: PM-179: saving the article remounts the panel (deactivate, then activate for the same
    // article). A result already held for it is kept: the restore may replace it with a run it
    // finds, but a no_run or a failure must not wipe what the editor is working through.
    const keepsResult =
      current.articleId === articleId && current.status === 'complete';
    this.isActive = true;
    const epoch = ++this.activationEpoch;
    this.stopPolling();
    this.restoreSub?.unsubscribe();
    if (!keepsResult) {
      this.stateSubject.next({ ...INITIAL_STATE, articleId });
    }

    this.restoreSub = this.smartLinkingService
      .getSmartLinks$(articleId)
      .subscribe({
        next: (result) => {
          if (epoch !== this.activationEpoch) {
            return;
          }
          if (result.kind === 'running') {
            this.patchState({ status: 'running', runId: result.runId });
            this.startPolling(articleId, (settled) =>
              this.settleRun(settled, { status: 'idle' }),
            );
          } else if (result.kind === 'complete') {
            this.patchState({
              status: 'complete',
              runId: result.runId,
              smartLinks: result.smartLinks,
            });
          }
          // NOTE: no_run and failed leave the state alone: idle on the never-ran path, or a kept
          // result. A run that failed before the editor arrived is theirs to re-run, not an error.
        },
        error: () => {
          if (epoch !== this.activationEpoch || keepsResult) {
            return;
          }
          // Restore failures degrade to idle: page load never greets the user with an error
          // for a feature they have not touched yet.
          this.patchState({ status: 'idle' });
        },
      });
  }

  activateUnsaved(): void {
    this.isActive = true;
    this.cancelRequests();
    this.stateSubject.next(INITIAL_STATE);
  }

  deactivate(): void {
    this.isActive = false;
    this.cancelRequests();
  }

  private cancelRequests(): void {
    this.activationEpoch++;
    this.restoreSub?.unsubscribe();
    this.restoreSub = null;
    this.stopPolling();
  }

  // Every caller runs straight against the gateway's live content — a run itself never saves the
  // article, so there is nothing here to wait on before calling postRun.
  run(gateway: SmartLinkArticleGateway): void {
    const state = this.stateSubject.value;
    if (
      !state.articleId ||
      state.status === 'triggering' ||
      state.status === 'running' ||
      gateway.isSaving()
    ) {
      return;
    }
    this.postRun(state.articleId, gateway, this.activationEpoch);
  }

  // NOTE: the API replays identical text, so run_in_flight always means another tab or editor is
  // scanning a different version of this article: wait for it, then scan our own text.
  private postRun(
    articleId: string,
    gateway: SmartLinkArticleGateway,
    epoch: number,
    waitsForOtherRun = 0,
  ): void {
    if (waitsForOtherRun === 0) {
      this.patchState({
        status: 'triggering',
        smartLinks: [],
        errorReason: null,
      });
    }
    const body: ISmartLinkRunRequest = {
      content: gateway.getContent(),
      article_saved_at: gateway.getSavedAt(),
    };

    this.smartLinkingService.triggerRun$(articleId, body).subscribe({
      next: (result) => {
        if (epoch !== this.activationEpoch) {
          return;
        }
        this.patchState({ status: 'running', runId: result.runId });
        if (!result.wasAlreadyRunning) {
          this.startPolling(articleId, (settled) =>
            this.settleRun(settled, {
              status: 'error',
              errorReason: 'run_lost',
            }),
          );
          return;
        }
        if (waitsForOtherRun >= SMART_LINK_MAX_WAITS_FOR_OTHER_RUN) {
          this.patchState({ status: 'error', errorReason: 'unavailable' });
          return;
        }
        this.startPolling(articleId, () =>
          this.postRun(articleId, gateway, epoch, waitsForOtherRun + 1),
        );
      },
      error: () => {
        if (epoch !== this.activationEpoch) {
          return;
        }
        this.patchState({ status: 'error', errorReason: 'unavailable' });
      },
    });
  }

  accept(smartLinkId: string): void {
    this.decide(smartLinkId, SmartLinkDecisionAction.ACCEPT);
  }

  reject(smartLinkId: string, reason: SmartLinkRejectReason): void {
    this.decide(smartLinkId, SmartLinkDecisionAction.REJECT, reason);
  }

  // Revokes server-side: the rejection is lifted, so a reload leaves the card in the queue.
  undoReject(smartLinkId: string): void {
    this.undoDecision(smartLinkId, SmartLinkDecisionAction.REJECT);
  }

  // The caller must remove the record from Related data first, or the record the accept wrote
  // stays in its box while the suggestion returns to the queue: both linked and pending.
  undoAccept(smartLinkId: string): void {
    this.undoDecision(smartLinkId, SmartLinkDecisionAction.ACCEPT);
  }

  // Optimistic: the card returns to the queue immediately and goes back if the server refuses.
  // revoke covers both verdicts — an undone reject has no edge to delete but still lifts
  // suppression, so a later reload does not re-apply it.
  private undoDecision(
    smartLinkId: string,
    action: SmartLinkDecisionAction,
  ): void {
    const state = this.stateSubject.value;
    // decide()'s callback replaces the card with server truth, which would silently re-apply a
    // decision undone while its POST is still open.
    if (state.decisionsInFlight.includes(smartLinkId)) {
      return;
    }
    const item = state.smartLinks.find(
      (link) => link.smart_link_id === smartLinkId,
    );
    // Guarded on the action the caller expects, so an undo can never cross verdicts.
    if (item?.decision_action !== action || !state.articleId) {
      return;
    }
    const articleId = state.articleId;

    this.replaceItem({
      ...item,
      decision_action: null,
      decision_reason: null,
    });
    this.patchState({
      decisionsInFlight: [...state.decisionsInFlight, smartLinkId],
    });

    this.smartLinkingService
      .revoke$(articleId, item)
      .pipe(
        finalize(() => {
          if (this.stateSubject.value.articleId !== articleId) {
            return;
          }
          this.patchState({
            decisionsInFlight: this.stateSubject.value.decisionsInFlight.filter(
              (id) => id !== smartLinkId,
            ),
          });
        }),
      )
      .subscribe({
        next: (result) => {
          if (this.stateSubject.value.articleId !== articleId) {
            return;
          }
          if (result.kind === 'not_revoked') {
            this.replaceItem(item);
            this.decisionError$.next({
              smartLinkId,
              action,
              phase: 'undo',
              reason: 'not_revoked',
            });
          }
        },
        error: () => {
          if (this.stateSubject.value.articleId !== articleId) {
            return;
          }
          this.replaceItem(item);
          this.decisionError$.next({
            smartLinkId,
            action,
            phase: 'undo',
            reason: 'unavailable',
          });
        },
      });
  }

  private startPolling(
    articleId: string,
    onSettled: (result: SmartLinkSettledFetch) => void,
  ): void {
    this.stopPolling();
    let consecutiveFailures = 0;
    let settled: SmartLinkSettledFetch | null = null;
    this.pollSub = timer(0, SMART_LINK_POLL_INTERVAL_MS)
      .pipe(
        // Overall deadline: rxjs timeout() would never fire because every poll emits.
        takeUntil(timer(SMART_LINK_POLL_DEADLINE_MS)),
        // exhaustMap: a GET slower than the interval skips ticks instead of being cancelled. The
        // catchError is inside it so a failed tick ends that request, not the timer.
        exhaustMap(() =>
          this.smartLinkingService
            .getSmartLinks$(articleId)
            .pipe(catchError(() => of(POLL_TICK_FAILED))),
        ),
        takeWhile(
          (result) => result === POLL_TICK_FAILED || result.kind === 'running',
          true,
        ),
      )
      .subscribe({
        next: (result) => {
          if (result === POLL_TICK_FAILED) {
            consecutiveFailures++;
            if (
              consecutiveFailures >= SMART_LINK_POLL_MAX_CONSECUTIVE_FAILURES
            ) {
              this.patchState({ status: 'error', errorReason: 'unavailable' });
              this.stopPolling();
            }
            return;
          }
          consecutiveFailures = 0;
          if (result.kind !== 'running') {
            settled = result;
          }
        },
        error: () =>
          this.patchState({ status: 'error', errorReason: 'unavailable' }),
        complete: () => {
          // External stops unsubscribe rather than complete, so reaching here unsettled means
          // the deadline elapsed.
          if (!settled) {
            this.patchState({ status: 'error', errorReason: 'unavailable' });
            return;
          }
          onSettled(settled);
        },
      });
  }

  private settleRun(
    result: SmartLinkSettledFetch,
    whenLost: Partial<ISmartLinkRunState>,
  ): void {
    if (result.kind === 'complete') {
      this.patchState({
        status: 'complete',
        runId: result.runId,
        smartLinks: result.smartLinks,
      });
      return;
    }
    if (result.kind === 'failed') {
      this.patchState({
        status: 'error',
        runId: result.runId,
        errorReason: 'run_failed',
      });
      return;
    }
    this.patchState(whenLost);
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  private decide(
    smartLinkId: string,
    action: SmartLinkDecisionAction,
    reason?: SmartLinkRejectReason,
  ): void {
    const state = this.stateSubject.value;
    if (state.decisionsInFlight.includes(smartLinkId)) {
      return;
    }
    const item = state.smartLinks.find(
      (link) => link.smart_link_id === smartLinkId,
    );
    // The card is the fallback the service returns unchanged if a 409 cannot name what the
    // server actually holds.
    if (!item || !state.articleId) {
      return;
    }
    const articleId = state.articleId;
    // NOTE: PM-179: a decision is scoped to its article, not to the activation, so a response that
    // lands after the save remount still settles the card. Only a switch to another article drops it.
    this.patchState({
      decisionsInFlight: [...state.decisionsInFlight, smartLinkId],
    });

    this.smartLinkingService
      .decide$(articleId, item, action, reason)
      .pipe(
        finalize(() => {
          if (this.stateSubject.value.articleId !== articleId) {
            return;
          }
          this.patchState({
            decisionsInFlight: this.stateSubject.value.decisionsInFlight.filter(
              (id) => id !== smartLinkId,
            ),
          });
        }),
      )
      .subscribe({
        // The card is always replaced with server truth. A mismatch — already_decided, or a
        // decided whose echo disagrees — means this attempt did not take, and is never
        // transient: the suggestion already holds a decision. acceptSuggestion wrote a box
        // record ahead of the POST, so a mismatched ACCEPT needs rolling back; the panel does
        // that, keyed off this emission.
        next: (result) => {
          if (this.stateSubject.value.articleId !== articleId) {
            return;
          }
          this.replaceItem(result.smartLink);
          if (result.smartLink.decision_action !== action) {
            this.decisionError$.next({
              smartLinkId,
              action,
              phase: 'decide',
              reason: 'already_decided',
            });
          }
        },
        error: () => {
          if (this.stateSubject.value.articleId !== articleId) {
            return;
          }
          this.decisionError$.next({
            smartLinkId,
            action,
            phase: 'decide',
            reason: 'unavailable',
          });
        },
      });
  }

  private replaceItem(item: ISmartLink): void {
    this.patchState({
      smartLinks: this.stateSubject.value.smartLinks.map((link) =>
        link.smart_link_id === item.smart_link_id ? item : link,
      ),
    });
  }

  private patchState(patch: Partial<ISmartLinkRunState>): void {
    this.stateSubject.next({ ...this.stateSubject.value, ...patch });
  }
}
